import { useState, useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input, Label, cn } from '@pantry/ui';
import { PlusIcon } from '@heroicons/react/24/outline';
import {
  useStorageLocations,
  useCreateStorageLocation,
  type ItemUnit,
  type StorageLocationType,
  ITEM_UNITS,
  UNIT_DISPLAY_LABELS,
  STORAGE_LOCATION_TYPES,
} from '@pantry/api-client';
import { looksLikeBarcode } from '@pantry/shared/utils';
import { toast } from 'sonner';
import { logger } from '../../../lib/logger';

interface ItemFieldsSectionProps {
  householdId: string;
  customName: string;
  onCustomNameChange: (name: string) => void;
  unit: ItemUnit;
  onUnitChange: (unit: ItemUnit) => void;
  storageLocationId: string;
  onStorageLocationIdChange: (id: string) => void;
  barcode?: string;
  onBarcodeChange?: (barcode: string) => void;
  showBarcode: boolean;
  notes: string;
  onNotesChange: (notes: string) => void;
  quantity: string;
  onQuantityChange: (q: string) => void;
  hasProduct?: boolean;
}

export function ItemFieldsSection({
  householdId,
  customName,
  onCustomNameChange,
  unit,
  onUnitChange,
  storageLocationId,
  onStorageLocationIdChange,
  barcode = '',
  onBarcodeChange,
  showBarcode,
  notes,
  onNotesChange,
  quantity,
  onQuantityChange,
  hasProduct = false,
}: ItemFieldsSectionProps) {
  const { t } = useLingui();

  // Storage location hooks
  const { data: storageLocations, isLoading: locationsLoading } = useStorageLocations(householdId);
  const createLocation = useCreateStorageLocation();

  // Inline storage location creation state
  const [showCreateLocation, setShowCreateLocation] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationType, setNewLocationType] = useState<StorageLocationType>('pantry');

  const getStorageTypeLabel = (type: StorageLocationType): string => {
    switch (type) {
      case 'fridge':
        return t`Fridge`;
      case 'freezer':
        return t`Freezer`;
      case 'pantry':
        return t`Pantry`;
      case 'other':
        return t`Other`;
      default:
        return type;
    }
  };

  // Auto-select first storage location if none selected
  useEffect(() => {
    if (
      storageLocations &&
      storageLocations.length > 0 &&
      !storageLocationId &&
      !showCreateLocation
    ) {
      onStorageLocationIdChange(storageLocations[0].id);
    }
  }, [storageLocations, storageLocationId, showCreateLocation, onStorageLocationIdChange]);

  const handleCreateLocation = async () => {
    if (!newLocationName.trim()) return;

    try {
      const newLocation = await createLocation.mutateAsync({
        householdId,
        data: {
          name: newLocationName.trim(),
          type: newLocationType,
        },
      });
      onStorageLocationIdChange(newLocation.id);
      setNewLocationName('');
      setNewLocationType('pantry');
      setShowCreateLocation(false);
    } catch (err) {
      logger.error('Failed to create storage location', err, { householdId });
      toast.error(t`Failed to create storage location`);
    }
  };

  return (
    <>
      {/* Custom Name */}
      <div className="space-y-2">
        <Label htmlFor="customName">
          {hasProduct ? (
            <Trans>Display Name</Trans>
          ) : (
            <>
              <Trans>Custom Name</Trans> *
            </>
          )}
        </Label>
        <Input
          id="customName"
          type="text"
          placeholder={hasProduct ? t`Enter display name...` : t`Enter item name...`}
          value={customName}
          onChange={(e) => onCustomNameChange(e.target.value)}
        />
      </div>

      {/* UPC/Barcode Field */}
      {showBarcode && (
        <div className="space-y-2">
          <Label htmlFor="upcCode">
            <Trans>UPC/Barcode (optional)</Trans>
          </Label>
          <Input
            id="upcCode"
            type="text"
            placeholder={t`Enter barcode...`}
            value={barcode}
            onChange={(e) => onBarcodeChange?.(e.target.value)}
            maxLength={14}
          />
          {barcode && !looksLikeBarcode(barcode) && (
            <p className="text-xs text-destructive">
              <Trans>Barcode should be 8-14 digits</Trans>
            </p>
          )}
        </div>
      )}

      {/* Quantity and Unit */}
      <div className="grid grid-cols-2 gap-4" data-tour="inventory-form-quantity">
        <div className="space-y-2">
          <Label htmlFor="quantity">
            <Trans>Quantity</Trans>
          </Label>
          <Input
            id="quantity"
            type="number"
            min="1"
            placeholder="1"
            value={quantity}
            onChange={(e) => onQuantityChange(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="unit">
            <Trans>Unit</Trans>
          </Label>
          <select
            id="unit"
            className={cn(
              'flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm',
              'ring-offset-background transition-colors duration-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'hover:border-primary/50'
            )}
            value={unit}
            onChange={(e) => onUnitChange(e.target.value as ItemUnit)}
          >
            {ITEM_UNITS.map((u) => (
              <option key={u} value={u}>
                {UNIT_DISPLAY_LABELS[u]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Storage Location */}
      <div className="space-y-2" data-tour="inventory-form-storage">
        <Label htmlFor="location">
          <Trans>Storage Location</Trans> *
        </Label>
        {locationsLoading ? (
          <div className="h-10 w-full animate-pulse rounded-xl bg-muted" />
        ) : (
          <>
            {/* Dropdown with "Add new" option */}
            {storageLocations && storageLocations.length > 0 && !showCreateLocation && (
              <select
                id="location"
                className={cn(
                  'flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm',
                  'ring-offset-background transition-colors duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  'hover:border-primary/50'
                )}
                value={storageLocationId}
                onChange={(e) => {
                  if (e.target.value === '__create_new__') {
                    setShowCreateLocation(true);
                    onStorageLocationIdChange('');
                  } else {
                    onStorageLocationIdChange(e.target.value);
                  }
                }}
              >
                <option value="">{t`Select a location`}</option>
                {storageLocations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name} ({getStorageTypeLabel(loc.type)})
                  </option>
                ))}
                <option value="__create_new__">{t`+ Add new location...`}</option>
              </select>
            )}

            {/* Inline create form - shown when no locations OR user clicked "Add new" */}
            {(showCreateLocation || !storageLocations || storageLocations.length === 0) && (
              <div className="rounded-xl border border-dashed border-primary/30 p-4 space-y-3 bg-primary/5 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">
                    {storageLocations?.length ? (
                      <Trans>Add New Location</Trans>
                    ) : (
                      <Trans>Create Your First Storage Location</Trans>
                    )}
                  </p>
                  {storageLocations && storageLocations.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowCreateLocation(false)}
                    >
                      <Trans>Cancel</Trans>
                    </Button>
                  )}
                </div>

                {!storageLocations?.length && (
                  <p className="text-xs text-muted-foreground">
                    <Trans>
                      You need at least one storage location to add items. Create one now:
                    </Trans>
                  </p>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="newLocationName" className="text-xs">
                      <Trans>Name</Trans>
                    </Label>
                    <Input
                      id="newLocationName"
                      type="text"
                      placeholder={t`e.g., Kitchen Fridge`}
                      value={newLocationName}
                      onChange={(e) => setNewLocationName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="newLocationType" className="text-xs">
                      <Trans context="form_label">Type</Trans>
                    </Label>
                    <select
                      id="newLocationType"
                      className={cn(
                        'flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm',
                        'ring-offset-background transition-colors duration-200',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        'hover:border-primary/50'
                      )}
                      value={newLocationType}
                      onChange={(e) => setNewLocationType(e.target.value as StorageLocationType)}
                    >
                      {STORAGE_LOCATION_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {getStorageTypeLabel(type)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <Button
                  type="button"
                  size="sm"
                  onClick={handleCreateLocation}
                  disabled={!newLocationName.trim() || createLocation.isPending}
                  className="w-full"
                >
                  {createLocation.isPending ? (
                    <>
                      <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      <Trans>Creating...</Trans>
                    </>
                  ) : (
                    <>
                      <PlusIcon className="h-4 w-4 mr-2" />
                      <Trans>Create Location</Trans>
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="notes">
          <Trans>Notes (optional)</Trans>
        </Label>
        <Input
          id="notes"
          type="text"
          placeholder={t`Any additional notes...`}
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
        />
      </div>
    </>
  );
}
